/** Maps become-advisor UI topic labels to canonical `advisorCategories` keys. */
export const ADVISE_TOPIC_TO_CATEGORY_KEY: Record<string, string> = {
  Funding: 'funding',
  Fundraising: 'funding',
  'Customer acquisition': 'customer-acquisition',
  Product: 'product',
  'Engineering & tech': 'engineering-tech',
  'Engineering & Tech': 'engineering-tech',
  'Team & hiring': 'team-hiring',
  'Team & Hiring': 'team-hiring',
  'Ops & finance': 'ops-finance',
  'Ops & Finance': 'ops-finance',
  'Marketing & brand': 'design-brand',
  'Marketing & Brand': 'design-brand',
  Legal: 'legal',
  Leadership: 'leadership',
  'Leadership & Strategy': 'leadership',
}

export const adviseTopicsToCategoryKeys = (topics: Iterable<string>): string[] => {
  const keys = new Set<string>()
  for (const topic of topics) {
    const key = ADVISE_TOPIC_TO_CATEGORY_KEY[topic]
    if (key) {
      keys.add(key)
    }
  }
  return [...keys]
}

/** Apply-form labels for stored `AdvisorProfile.categoryKeys`. First alias wins. */
export const CATEGORY_KEY_TO_ADVISE_TOPIC: Record<string, string> = Object.entries(
  ADVISE_TOPIC_TO_CATEGORY_KEY
).reduce<Record<string, string>>((acc, [topic, key]) => {
  if (!acc[key]) {
    acc[key] = topic
  }
  return acc
}, {})

export const categoryKeysToAdviseTopics = (keys: Iterable<string | null | undefined>): string[] => {
  const topics: string[] = []
  const seen = new Set<string>()
  for (const key of keys) {
    if (!key) {
      continue
    }
    const topic = CATEGORY_KEY_TO_ADVISE_TOPIC[key]
    if (topic && !seen.has(topic)) {
      seen.add(topic)
      topics.push(topic)
    }
  }
  return topics
}

/** Maps seat/directory category display names to `categoryKey` values. */
export const CATEGORY_NAME_TO_KEY: Record<string, string> = {
  Funding: 'funding',
  'Customer acquisition': 'customer-acquisition',
  Product: 'product',
  'Engineering & Tech': 'engineering-tech',
  'Team & Hiring': 'team-hiring',
  'Operations & Finance': 'ops-finance',
  'Design & Brand': 'design-brand',
  Legal: 'legal',
  'Leadership & Strategy': 'leadership',
}

export const categoryNameToKey = (name: string): string | undefined => CATEGORY_NAME_TO_KEY[name]

export const CATEGORY_KEY_TO_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_NAME_TO_KEY).map(([name, key]) => [key, name])
)

/** Maps become-advisor warm-intro UI labels to canonical keys. */
export const WARM_INTRO_LABEL_TO_KEY: Record<string, string> = {
  Investors: 'investors',
  Customers: 'customers',
  Talent: 'talent',
}

/**
 * Maps engagement / availability chip labels to default hours per month.
 * Includes mockup "How can Founders engage you?" labels + legacy labels for rehydrate.
 */
export const AVAILABILITY_HOURS_BY_LABEL: Record<string, number> = {
  'Scheduled calls': 5,
  Email: 2,
  'Chat / DMs': 1,
  'In person': 10,
  // Legacy become-advisor labels
  'Office hours (1:1)': 2,
  'Group workshops': 5,
  'Async questions': 1,
  'Ongoing board seat': 20,
}

/** Inverse of {@link AVAILABILITY_HOURS_BY_LABEL} for profile rehydrate. */
export const AVAILABILITY_LABEL_BY_HOURS: Record<number, string> = Object.fromEntries(
  Object.entries(AVAILABILITY_HOURS_BY_LABEL).map(([label, hours]) => [hours, label])
)

export const availabilityLabelsToHours = (labels: Iterable<string>): number | undefined => {
  const hours = [...labels]
    .map(label => AVAILABILITY_HOURS_BY_LABEL[label])
    .filter((value): value is number => value != null)
  if (hours.length === 0) {
    return undefined
  }
  return Math.max(...hours)
}
